import { FormikProps } from 'formik';
import React, { useCallback, useEffect, useMemo, useRef, useState, VFC } from 'react';
import { preventSubmitOnEnterPressed } from 'src/common/helpers/prevent-submit-on-enter-pressed';
import { JourneyForm, JourneyFormik } from 'src/common/JourneyFormik';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import {
  CONTENT_BEHAVIOR_DISPLAY_IN_JOURNEY,
  STEP_EDITOR_CONTENT_OPTION_RECORD,
  CONTENT_BEHAVIOR_EXTERNAL_LINK,
  CONTENT_TYPE_FILE,
} from 'src/editor/constants';
import { useEditorNavigator } from 'src/editor/use-editor-navigator.hook';
import { ChevronRightStrongIcon } from 'src/monet/icons';
import { useStepEditorContext } from 'src/store/step-editor';
import { usePreviousValue } from 'src/utils/react/previous-value.hook';
import * as Yup from 'yup';
import { useSubmitStepForm } from '../../use-submit-step-form';
import { StepEditorContentAnnotationPanel } from '../annotation/panel';
import { DefaultContentBehaviorOptions, StepEditorContentBehaviorPanel } from '../behavior/panel';
import { StepEditorContentFormSubmitButtons } from '../form/submit-buttons';
import { StepEditorContentFormSyncedStepCopy } from '../form/synced-step-copy';
import { StepEditorSection } from '../section';
import { StepEditorContentConfigureStageTypeEnum } from './stage-type.enum';
import { StepEditorContentConfigureTitle } from './title';
import { useEditorContext } from 'src/store/editor';
import { EventBus } from 'src/common/EventBus';
import { Node } from 'src/common/interfaces/node.interface';
import { useUpdateEffect } from 'react-use';

export interface StepEditorConfigureForm {
  name: string;
}

const validationSchema = Yup.object({
  name: Yup.string().required('Steps require a name to be saved'),
});

interface StepEditorContentConfigurePanelProps {
  activeStage: StepEditorContentConfigureStageTypeEnum;
}

export const StepEditorContentConfigurePanel: VFC<StepEditorContentConfigurePanelProps> = ({ activeStage }) => {
  const isMountRef = useRef(false);
  const { currentStep, setCurrentActiveNodeId, currentActiveNodeId } = useEditorContext();
  const { navigateToConfigurationStage, navigateToJourneyEdit } = useEditorNavigator();
  const {
    name,
    setName,
    link,
    node_id,
    data,
    setOldContentBehavior,
    setStateFromNode,
    resetOldContentBehavior,
    setData,
    isPlaceholder,
    contentOption,
    contentName,
    contentType,
    annotation,
    contentBehavior,
    setCurrentConfigurationStage,
  } = useStepEditorContext();
  const prevNameValue = usePreviousValue(name);
  const prevNodeId = usePreviousValue(node_id);
  const { setSuccessNotification } = useNotificationMessage();
  const { submitForm } = useSubmitStepForm();
  const [showPanel, setShowPanel] = useState(false);

  const currentNodeContent = currentStep?.nodes.find((node) => node.id === currentActiveNodeId);

  useEffect(() => {
    if (!currentNodeContent) {
      return;
    }
    if (Object.keys(currentNodeContent.data || {}).includes('downloadable')) {
      setData({
        downloadable: (currentNodeContent.data?.downloadable as boolean) || false,
      });
    }
  }, [currentNodeContent]);

  const isNewStepActive = useMemo(
    () => window.location.pathname.endsWith(StepEditorContentConfigureStageTypeEnum.NEW),
    []
  );

  useEffect(() => {
    // navigation changes
    if (isMountRef.current && prevNodeId && prevNodeId !== node_id) {
      isMountRef.current = false;
    }
  }, [node_id, prevNodeId, name, setName, annotation, contentName, link, contentBehavior]);

  useEffect(() => {
    if (node_id) {
      setOldContentBehavior(contentBehavior);
    }
  }, [setOldContentBehavior, contentName, contentBehavior, node_id]);

  const formikParams = {
    initialValues: { name: name || '' },
    validationSchema,
    onSubmit: (values: StepEditorConfigureForm) => {
      submitForm(values, (response: Node) => {
        resetOldContentBehavior();

        if (currentStep) {
          EventBus.emit('editor:reset-stage', { showInitialStage: false });
          if (isNewStepActive) {
            navigateToJourneyEdit({ replaceHistory: true });
          } else {
            setSuccessNotification('Step successfully updated');
            navigateToJourneyEdit();
          }
          setCurrentActiveNodeId(response.id);
          setStateFromNode(response);
        }
      });
    },
  };

  const updateActiveConfigurationStage = useCallback(
    (stage: StepEditorContentConfigureStageTypeEnum) => {
      setCurrentConfigurationStage(stage);
      if (!isNewStepActive) {
        navigateToConfigurationStage(stage);
      }
    },
    [setCurrentConfigurationStage, isNewStepActive, navigateToConfigurationStage]
  );

  const onAnnotationSectionClicked = useCallback(() => {
    updateActiveConfigurationStage(StepEditorContentConfigureStageTypeEnum.ANNOTATION);
  }, [updateActiveConfigurationStage]);

  const onContentBehaviorClicked = useCallback(() => {
    updateActiveConfigurationStage(StepEditorContentConfigureStageTypeEnum.BEHAVIOR);
  }, [updateActiveConfigurationStage]);

  const activeContentBehavior = useMemo(() => {
    return contentType === CONTENT_TYPE_FILE
      ? CONTENT_BEHAVIOR_EXTERNAL_LINK
      : contentOption === STEP_EDITOR_CONTENT_OPTION_RECORD
      ? CONTENT_BEHAVIOR_DISPLAY_IN_JOURNEY
      : contentBehavior || CONTENT_BEHAVIOR_DISPLAY_IN_JOURNEY;
  }, [contentBehavior, contentType]);

  const contentBehaviorSubtitle = useMemo(() => {
    if (contentType === CONTENT_TYPE_FILE) {
      return 'Control how your content looks';
    }

    const activeContentBehavior = contentBehavior ?? CONTENT_BEHAVIOR_DISPLAY_IN_JOURNEY;
    return DefaultContentBehaviorOptions.find(({ value }) => value === activeContentBehavior)!.label as string;
  }, [contentBehavior, contentType]);

  // if a memoized function is not passed along, synced step copy fetches multiple times
  const onCopyReady = useCallback(() => {
    setShowPanel(true);
  }, []);

  const onChangeName = (newName: string) => {
    setName(newName);
  };

  const renderConfigureContent = useCallback(
    (values: StepEditorConfigureForm, { errors }: { errors: Record<string, string> }) => {
      if (activeStage === StepEditorContentConfigureStageTypeEnum.CONFIGURE) {
        return (
          <>
            <div className='h-px bg-bedrock-gray-medium w-full rounded-[100px]' />
            <StepEditorContentFormSyncedStepCopy onReady={onCopyReady} />
            {showPanel && (
              <>
                <h1 className='heading-bedrock-tertiary'>Configure</h1>
                <div className='flex flex-col space-y-lg'>
                  <StepEditorContentConfigureTitle
                    onChange={onChangeName}
                    fieldName='name'
                    value={values.name}
                    errorText={errors.name}
                  />
                  <div className='h-px bg-bedrock-gray-medium w-full mt-6 rounded-[100px]' />
                  <StepEditorSection
                    title='Annotation'
                    wrapperClassName='mt-px'
                    subtitleClassName='-mt-px pb-lg'
                    subTitle={annotation?.text || 'Describe your content to recipients'}
                    onClick={onAnnotationSectionClicked}
                    renderBadge={() => <ChevronRightStrongIcon />}
                  />
                  <StepEditorSection
                    hideBorderBottom
                    title='Content behavior'
                    wrapperClassName='mt-px'
                    subtitleClassName='-mt-px pb-lg'
                    subTitle={contentBehaviorSubtitle}
                    onClick={onContentBehaviorClicked}
                    renderBadge={() => <ChevronRightStrongIcon />}
                  />
                  <StepEditorContentFormSubmitButtons />
                </div>
              </>
            )}
          </>
        );
      } else if (activeStage === StepEditorContentConfigureStageTypeEnum.ANNOTATION) {
        return <StepEditorContentAnnotationPanel />;
      } else if (activeStage === StepEditorContentConfigureStageTypeEnum.BEHAVIOR) {
        return (
          <StepEditorContentBehaviorPanel value={activeContentBehavior} downloadable={data?.downloadable || false} />
        );
      }

      return null;
    },
    [
      activeStage,
      onCopyReady,
      showPanel,
      data,
      setName,
      annotation,
      onAnnotationSectionClicked,
      contentBehaviorSubtitle,
      onContentBehaviorClicked,
      isPlaceholder,
      activeContentBehavior,
    ]
  );

  return (
    <JourneyFormik {...formikParams}>
      {({ values, setFieldValue, errors }: FormikProps<StepEditorConfigureForm>) => {
        if (prevNameValue && !isMountRef.current && prevNameValue !== name) {
          setFieldValue('name', name);
          isMountRef.current = true;
        }

        return (
          <JourneyForm onKeyPress={preventSubmitOnEnterPressed}>
            <div className='flex flex-col pb-lg space-y-lg'>
              {renderConfigureContent(values, {
                errors,
              })}
            </div>
          </JourneyForm>
        );
      }}
    </JourneyFormik>
  );
};
